import { CreatePartnerSchema } from "@biteasy/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { ErrorMessage, Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import { Text } from "@/components/catalyst/Text";
import { useCanCreate } from "@/hooks/use-subscription";
import { orpc } from "@/orpc/client";

interface PartnerCreateFormDialogProps {
	isOpen: boolean;
	onClose: () => void;
	appId?: string;
}

export function PartnerCreateFormDialog({
	isOpen,
	onClose,
	appId: propAppId,
}: PartnerCreateFormDialogProps) {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const {
		canCreate,
		usage,
		limit,
		isLoading: isLoadingLimits,
	} = useCanCreate("partners");

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		formState: { errors, isSubmitting },
	} = useForm<z.infer<typeof CreatePartnerSchema>>({
		resolver: zodResolver(CreatePartnerSchema),
		defaultValues: {
			appId: propAppId,
		},
	});

	const { data: apps } = useQuery(orpc.apps.list.queryOptions({}));

	const mutation = useMutation(
		orpc.partners.create.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: orpc.partners.list.key() });
				queryClient.invalidateQueries({
					queryKey: orpc.billing.getSubscription.key(),
				});
				toast.success("Partner created successfully");
				reset();
				onClose();
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const onSubmit: SubmitHandler<z.infer<typeof CreatePartnerSchema>> = (
		data,
	) => {
		// If propAppId is provided, force it (although defaultValues should handle it)
		if (propAppId) {
			data.appId = propAppId;
		}
		// If appId is empty string (from select "None"), make it undefined
		if (data.appId === "") {
			data.appId = undefined;
		}
		mutation.mutate(data);
	};

	const handleUpgrade = () => {
		onClose();
		navigate({ to: "/settings", search: { tab: "billing" } });
	};

	// Show limit reached UI
	if (!isLoadingLimits && !canCreate) {
		return (
			<Dialog open={isOpen} onClose={onClose}>
				<DialogTitle>Partner Limit Reached</DialogTitle>
				<DialogBody>
					<div className="text-center py-4">
						<div className="mx-auto w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center mb-4">
							<AlertTriangle className="size-6 text-warning" />
						</div>
						<Text className="mb-2">
							You've used{" "}
							<strong>
								{usage}/{limit === Infinity ? "∞" : limit}
							</strong>{" "}
							partners on your current plan.
						</Text>
						<Text className="text-base-content/60">
							Upgrade your plan to create more partners.
						</Text>
					</div>
				</DialogBody>
				<DialogActions>
					<Button plain onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={handleUpgrade}>
						<ArrowUpRight className="size-4" />
						Upgrade Plan
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>Create Partner</DialogTitle>
			<DialogDescription>
				Add a new partner. You can optionally link them to an app.
			</DialogDescription>
			<DialogBody>
				<form
					id="partner-create-form"
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col gap-4"
				>
					<Field>
						<Label>Name</Label>
						<Input {...register("name")} placeholder="e.g. John Doe" />
						{errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
					</Field>

					<Field>
						<Label>Email</Label>
						<Input
							{...register("email")}
							type="email"
							placeholder="e.g. john@example.com"
						/>
						{errors.email && (
							<ErrorMessage>{errors.email.message}</ErrorMessage>
						)}
					</Field>

					{!propAppId && (
						<Field>
							<Label>App (Optional)</Label>
							<Select
								{...register("appId")}
								defaultValue=""
								onChange={(e) => {
									setValue("appId", e.target.value);
									// If clearing app, maybe clear payout fields or let them be?
									// Schema says payout optional if no appId.
								}}
							>
								<option value="">None</option>
								{apps?.map((app) => (
									<option key={app.id} value={app.id}>
										{app.name}
									</option>
								))}
							</Select>
						</Field>
					)}
				</form>
			</DialogBody>
			<DialogActions>
				<Button plain onClick={onClose}>
					Cancel
				</Button>
				<Button
					type="submit"
					form="partner-create-form"
					disabled={isSubmitting}
				>
					Create Partner
				</Button>
			</DialogActions>
		</Dialog>
	);
}
